<template>
  <ManageLanguages
    v-if="showManageLangsModal"
    :key="languagesUpdateKey"
    :data="shopsLangData"
    :id-shop="idShop"
    :translation="t"
    @reload-data="reloadLanguages"
    @close-action-modal="closeManageLangModal"
    @open-new-lang="openNewLang"
  />
  <AddNewLang
    v-if="showNewLangModal"
    :translation="t"
    :id-shop="idShop"
    :default-url="defaultUrl"
    :default-url-cart="defaultUrlCart"
    @close-action-modal="closeNewLangModal"
  />
  <div>
    <div class="p-fluid field">
      <label
        v-tooltip="t('storeParameters.storeInformations.tooltips.name')"
        for="name"
      >{{ t('storeParameters.storeInformations.name') }} <i class="fas fa-question-circle" /></label>
      <InputText
        id="name"
        v-model="shopsInfostate.name"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="name"
      />
    </div>

    <div class="p-fluid field">
      <label for="solution_ecommerce">{{ t('storeParameters.storeInformations.solution') }}</label>
      <Dropdown
        id="solution_ecommerce"
        v-model="shopsInfostate.solution_ecommerce"
        :options="solutions"
        option-label="label"
        option-value="code"
        :placeholder="t('storeParameters.storeInformations.solution')"
        disabled
      >
        <template #value="slotProps">
          <span v-if="slotProps.value">{{ solutionLabel(slotProps.value) }}</span>
        </template>
      </Dropdown>
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="solution_ecommerce"
      />
    </div>
    <div
      v-if="isShopify"
      class="p-fluid field"
    >
      <label
        v-tooltip="t('storeParameters.storeInformations.tooltips.birthdateMetafield')"
        for="birthdate_metafield"
      >{{ t('storeParameters.storeInformations.birthdateMetafield') }} <i class="fas fa-question-circle" /></label>
      <InputText
        id="birthdate_metafield"
        v-model="birthdateMetafield"
        placeholder="custom.birthday"
      />
    </div>
    <div class="p-fluid field">
      <label for="id_category_shop">{{ t('storeParameters.storeInformations.category') }}</label>
      <Dropdown
        id="id_category_shop"
        v-model="shopsInfostate.id_category_shop"
        :options="categories"
        option-label="label"
        option-value="code"
        :placeholder="t('storeParameters.storeInformations.category')"
      >
        <template #value="slotProps">
          <span v-if="slotProps.value">
            {{ t(categories.filter((category) => category.code === slotProps.value)[0].label) }}
          </span>
        </template>
        <template #option="slotProps">
          {{ t(slotProps.option.label) }}
        </template>
      </Dropdown>
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="id_category_shop"
      />
    </div>
    <div class="p-fluid field">
      <label for="currency">{{ t('storeParameters.storeInformations.currency') }}</label>
      <Dropdown
        id="currency"
        v-model="shopsInfostate.currency"
        :options="currencies"
        option-label="label"
        option-value="code"
        :placeholder="t('storeParameters.storeInformations.currency')"
        disabled
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="currency"
      />
    </div>
    <div class="p-fluid field">
      <label
        v-tooltip="t('storeParameters.storeInformations.tooltips.shopUrl')"
        for="url"
      >{{ t('storeParameters.storeInformations.shopUrl') }} <i class="fas fa-question-circle" /></label>
      <InputText
        id="shop_url"
        v-model="shopsInfostate.url"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="url"
      />
    </div>
    <div class="p-fluid field">
      <label for="timezone_name">{{ t('storeParameters.storeInformations.timezone') }}</label>
      <Dropdown
        id="timezone_name"
        v-model="shopsInfostate.timezone_name"
        :options="timezoneOptions"
        option-label="label"
        option-value="value"
        option-group-children="items"
        option-group-label="label"
        :filter="true"
        :placeholder="t('storeParameters.storeInformations.timezone')"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="timezone_name"
      />
    </div>
    <div class="p-fluid field">
      <label for="language">{{ t('storeParameters.storeInformations.language') }}</label>
      <Dropdown
        v-if="shopLanguages"
        v-model="shopsInfostate.default_lang"
        :options="shopLanguages"
        @change="({ value }) => {sendDefaultLang(value);}"
      >
        <template #value="slotProps">
          <img
            v-if="slotProps.value"
            :src="slotProps.value.value"
            alt="language-flag"
          >
            &nbsp;
          {{ slotProps.value.label ? t(`languages.codes.${slotProps.value.label}`): '' }}
        </template>
        <template #option="slotProps">
          <img
            v-if="slotProps.option"
            :src="slotProps.option.value"
            alt="language-flag"
          >
            &nbsp;
          {{ slotProps.option.label ? t(`languages.codes.${slotProps.option.label}`): '' }}
        </template>
      </Dropdown>
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="error"
        field="default_lang"
      />
    </div>
    <div class="flex justify-content-end ml-2">
      <Button
        @click="showManageLangsModal= true"
      >
        {{ t('storeParameters.storeInformations.languages.btnTitle') }}
      </Button>
    </div>
  </div>
  <div class="grid justify-items-end mt-5">
    <Button
      :label="t('save')"
      class="p-button p-button-success"
      type="submit"
      @click="saveStoreInfo"
    />
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, Ref, ref, watch,
} from 'vue';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { DropdownOption } from '@/types';
import categories from '@/configs/shop-categories';
import solutions from '@/configs/ecommerce-solutions';
import currencies from '@/configs/currencies';
import { Lang, loadShopsLang, UserState } from '@/composables/User';
import { getBirthdateMetafield, setBirthdateMetafield } from '@/composables/user/ShopifyConfig';
import fetchShopInformation, { ShopsLangCustom, updateShop } from '@/composables/shop/Shops';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import useVuelidate from '@vuelidate/core';
import { showToastError, showToastSuccess } from '@/helpers';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { ShopsLang } from '@/types/generated-types/graphql';
import ManageLanguages from '@/views/shop/tabs/modals/ManageLanguages.vue';
import AddNewLang from '@/views/shop/tabs/modals/AddNewLang.vue';
import { required, url } from '@vuelidate/validators';
import moment from 'moment';
import { getTimezoneOptions } from '@/components/template-builder/config/fields/timezone-options';

interface StoreInfoState {
  name: string;
  solution_ecommerce: string;
  id_category_shop: number;
  currency: string;
  url: string;
  url_client: string;
  default_lang: Record<string, any>;
  timezone_name: string;
}

interface ShopsLangState {
  url: Record<string, any>;
  url_cart: Record<string, any>;
}

export default defineComponent({
  name: 'StoreInformations',

  components: {
    AddNewLang,
    InputText,
    Dropdown,
    Button,
    LocalizedTextInput,
    FieldErrors,
    ManageLanguages,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idShop: {
      type: Number,
      required: true,
    },
  },

  emits: {
    'on-change': Object,
    'default-language': String,
    'refresh-component': String,
  },

  setup(props, { emit }) {
    const { t } = useI18n();
    const error = ref();
    const shopsLangData = ref<ShopsLang[]>([]);
    const componentFieldErrorsKey = ref(0);
    const showManageLangsModal = ref(false);
    const showNewLangModal = ref(false);
    const defaultUrl = ref();
    const defaultUrlCart = ref();
    const languagesUpdateKey = ref(0);
    const updateKey = ref(0);
    const needsToReloaded = ref(false);
    const shopLanguages = ref<DropdownOption[]>(UserState.activeShop?.langs ? UserState.activeShop?.langs.map((language: Lang) => ({
      label: language.id,
      value: `/images/flags/${language.id}.png`,
    })) : []);

    const shopsInfostate = reactive<StoreInfoState>({
      name: '',
      solution_ecommerce: '',
      id_category_shop: 0,
      currency: '',
      url: '',
      url_client: '',
      default_lang: {},
      timezone_name: '',
    });

    const timezoneOptions = getTimezoneOptions({ withOffset: true });

    const idShop = UserState.activeShop?.id ?? 0;

    // Metafield customer Shopify portant la date de naissance, saisi sous la
    // forme `namespace.key` (ex: custom.birthday). Vide = désactivé.
    const birthdateMetafield = ref('');
    const isShopify = computed(() => shopsInfostate.solution_ecommerce === 'shopify');

    // Libellé de la solution e-commerce (lecture seule) : certaines solutions
    // (shopify notamment) ne figurent pas dans les options du dropdown — on
    // retombe alors sur le code capitalisé.
    const solutionLabel = (code: string) => {
      const match = solutions.find((solution) => solution.code === code);
      return match ? match.label : code.charAt(0).toUpperCase() + code.slice(1);
    };
    const BIRTHDATE_METAFIELD_RE = /^[A-Za-z0-9_-]{2,64}\.[A-Za-z0-9_-]{2,64}$/;

    async function retrieveBirthdateMetafield() {
      if (!isShopify.value) {
        return;
      }
      const res = await getBirthdateMetafield(idShop);
      if (res && res.success && res.namespace && res.key) {
        birthdateMetafield.value = `${res.namespace}.${res.key}`;
      }
    }

    // Retourne false si la saisie est invalide ou si l'enregistrement a
    // échoué (toast déjà affiché), true sinon.
    async function saveBirthdateMetafield(): Promise<boolean> {
      if (!isShopify.value) {
        return true;
      }
      const input = birthdateMetafield.value.trim();
      if (input !== '' && !BIRTHDATE_METAFIELD_RE.test(input)) {
        await showToastError('storeParameters.storeInformations.birthdateMetafieldInvalid', 3000, true);
        return false;
      }
      const dotIndex = input.indexOf('.');
      const namespace = input === '' ? '' : input.substring(0, dotIndex);
      const key = input === '' ? '' : input.substring(dotIndex + 1);
      const res = await setBirthdateMetafield(idShop, namespace, key);
      if (!res || !res.success) {
        await showToastError('GENERIC_ERROR');
        return false;
      }
      return true;
    }

    const defaultLang = ref('');

    function sendDefaultLang(lang: string) {
      shopsLangData.value.forEach((shopsLang: ShopsLang) => {
        if (shopsLang.lang === lang) {
          defaultUrl.value = shopsLang.url;
          defaultUrlCart.value = shopsLang.url_cart;
        }
      });
      emit('default-language', { lang });
    }

    async function retrieveShopInfo() {
      const info = await fetchShopInformation({ shopId: idShop });
      shopsLangData.value = [];

      if (info === null) {
        return;
      }

      info.langs.forEach((shopsLang: ShopsLangCustom) => {
        shopsLangData.value.push({
          id_shop: idShop,
          lang: shopsLang.lang,
          url: shopsLang.url,
          url_cart: shopsLang.urlCart,
          default: shopsLang.default,
        });
      });

      defaultLang.value = info.defaultLang;
      shopsInfostate.name = info.name;
      shopsInfostate.solution_ecommerce = info.solutionEcommerce;
      shopsInfostate.id_category_shop = info.idCategoryShop;
      shopsInfostate.currency = info.currency;
      shopsInfostate.url = info.url;
      shopsInfostate.url_client = info.urlClient;
      shopsInfostate.default_lang = { label: info.defaultLang, value: `/images/flags/${info.defaultLang}.png` };
      shopsInfostate.timezone_name = info.timezoneName;
    }

    const validateShopInfo = async (): Promise<ErrorConfigForm> => {
      const rules = {
        name: {
          required,
        },

        solution_ecommerce: {
          required,
        },

        id_category_shop: {
          required,
        },

        currency: {
          required,
        },

        url: {
          required,
          url,
        },

        default_lang: {
          required,
        },

        timezone_name: {
          required,
        },

      };

      const v$ = useVuelidate(rules, shopsInfostate);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };

    const updateDefaultLangInGlobalState = () => {
      Object.entries(shopsInfostate)
        .every((keyValuePair: any) => {
          if (keyValuePair[0] === 'default_lang') {
            loadShopsLang(idShop);
            emit('refresh-component');
            return true;
          }
          return false;
        });
    };

    const saveStoreInfo = async () => {
      const shopArray: Record<string, any> = {};
      const shopValidations = await validateShopInfo();

      if (!shopValidations.success) {
        error.value = shopValidations.validate;
        componentFieldErrorsKey.value += 1;
        return;
      }

      Object.entries(shopsInfostate)
        .forEach((keyValuePair: any) => {
          if (keyValuePair[0] !== 'default_lang') {
            shopArray[keyValuePair[0]] = keyValuePair[1];
          }
        });

      shopArray.id_shop = idShop;
      shopArray.date_modification = moment().format('YYYY-MM-DD HH:mm:ss');

      if (!(await saveBirthdateMetafield())) {
        return;
      }

      updateShop([shopArray])
        .then(() => {
          updateDefaultLangInGlobalState();
          showToastSuccess('savedSuccessful');
        })
        .catch(async (err: any) => {
          await showToastError('GENERIC_ERROR');
        });
    };

    const openNewLang = () => {
      showManageLangsModal.value = false;
      showNewLangModal.value = true;
    };

    const closeNewLangModal = async (newRecordLang: ShopsLang, reload: boolean) => {
      showManageLangsModal.value = true;
      showNewLangModal.value = false;
      if (reload) {
        languagesUpdateKey.value += 1;
        needsToReloaded.value = true;
        shopsLangData.value.push(newRecordLang);
      }
    };

    const closeManageLangModal = async (reload: boolean) => {
      if (reload || needsToReloaded.value) {
        await retrieveShopInfo();
        emit('refresh-component');
        updateKey.value += 1;
      }
      showManageLangsModal.value = false;
      showNewLangModal.value = false;
      needsToReloaded.value = false;
      shopLanguages.value = UserState.activeShop?.langs ? UserState.activeShop?.langs.map((language: Lang) => ({
        label: language.id,
        value: `/images/flags/${language.id}.png`,
      })) : [];
    };

    const reloadLanguages = async () => {
      await retrieveShopInfo();
    };

    onMounted(async () => {
      await retrieveShopInfo();
      await retrieveBirthdateMetafield();
    });

    watch(shopsInfostate.default_lang, () => {
      sendDefaultLang(defaultLang.value);
    }, { deep: true });

    return {
      solutions,
      categories,
      currencies,
      shopLanguages,
      timezoneOptions,
      t,
      saveStoreInfo,
      sendDefaultLang,
      shopsInfostate,
      birthdateMetafield,
      isShopify,
      solutionLabel,
      error,
      componentFieldErrorsKey,
      showManageLangsModal,
      showNewLangModal,
      shopsLangData,
      openNewLang,
      closeNewLangModal,
      defaultUrl,
      defaultUrlCart,
      languagesUpdateKey,
      closeManageLangModal,
      reloadLanguages,

    };
  },
});
</script>

<style scoped lang="scss">
h3 {
  padding: 1.35rem 0;
}
</style>
